import {
  contractPagePreview, renderDunningLetterSvg, renderInvoiceSvg, renderOfferSvg,
  type BelegArt, type BelegLayout, type InvoicePdfModel, type PdfImage,
} from "@talentkontor/domain";

/**
 * Vorschau der PDF-Vorlagen (0456) mit Beispielwerten: dieselben Seiten wie das echte PDF (renderInvoiceSvg usw.), nur mit
 * einer erfundenen Marke und erfundenen Positionen. Name, Farbe und Logo kommen aus der Agentur, damit die Vorschau ihr gleicht.
 */

export interface VorschauAgentur {
  name: string;
  accent: string | null;
  logo: PdfImage | null;
  logoUrl: string | null;
  lang: "de" | "en";
}

const heute = () => new Date().toISOString().slice(0, 10);
const inTagen = (n: number) => new Date(Date.now() + n * 86_400_000).toISOString().slice(0, 10);

function absender(a: VorschauAgentur) {
  return {
    name: a.name || "Agentur",
    addressLines: ["Beispielweg 12", "20095 Hamburg"],
    fields: [{ label: a.lang === "en" ? "VAT ID" : "USt-IdNr.", value: "DE123456789" }],
  };
}
const empfaenger = { name: "Beispielmarke GmbH", addressLines: ["Musterstraße 1", "10115 Berlin"] };
const fuss = (a: VorschauAgentur) => [`${a.name || "Agentur"} · Beispielweg 12 · 20095 Hamburg · hallo@example.com`, "Geschäftsführung: Alex Beispiel · HRB 000000 Amtsgericht Hamburg"];
const bank = { accountHolder: "", iban: "DE89370400440532013000", bic: "COBADEFFXXX", bankName: "Beispielbank" };

export function rechnungBeispiel(a: VorschauAgentur, layout: BelegLayout, art: "rechnung" | "gutschrift" = "rechnung"): InvoicePdfModel {
  const lines = [
    { description: "Instagram Reel · Herbstkampagne · Nutzungsrechte 3 Monate", quantity: 2, unitPriceCents: 120_000, totalCents: 240_000 },
    { description: "Instagram Story (3 Frames)", quantity: 3, unitPriceCents: 25_000, totalCents: 75_000 },
  ];
  const net = 315_000, vat = 59_850;
  return {
    language: a.lang, locale: a.lang === "en" ? "en-GB" : "de-DE", currency: "EUR",
    number: art === "gutschrift" ? "GS-2026-007" : "RE-2026-042", invoiceDate: heute(), serviceMonth: heute().slice(0, 7), dueDate: inTagen(14),
    // Gutschrift: der Creator ist Aussteller (die Agentur schreibt sie in seinem Namen), die Agentur Empfängerin
    issuer: art === "gutschrift" ? { name: "Mara Linden", addressLines: ["Lindenallee 4", "50667 Köln"], fields: [{ label: a.lang === "en" ? "Tax number" : "Steuernummer", value: "215/5432/1234" }] } : absender(a),
    recipient: art === "gutschrift" ? { name: a.name || "Agentur", addressLines: ["Beispielweg 12", "20095 Hamburg"] } : empfaenger, lines,
    totals: { netCents: net, vatCents: vat, grossCents: net + vat, ratePercent: 19 },
    bank: { ...bank, accountHolder: art === "gutschrift" ? "Mara Linden" : a.name || "Agentur" }, footerLines: art === "gutschrift" ? [] : fuss(a), reference: art === "gutschrift" ? null : "PO-4711",
    paymentTermDays: art === "gutschrift" ? null : 14, selfBilling: art === "gutschrift",
    accent: a.accent, logo: a.logo, layout,
  };
}

export function vorschauSeiten(art: BelegArt, a: VorschauAgentur, layout: BelegLayout, rechnungArt: "rechnung" | "gutschrift" = "rechnung"): string[] {
  if (art === "invoice") return renderInvoiceSvg(rechnungBeispiel(a, layout, rechnungArt));
  if (art === "dunning") {
    return renderDunningLetterSvg({
      language: a.lang, locale: a.lang === "en" ? "en-GB" : "de-DE", currency: "EUR", sender: absender(a), footerLines: fuss(a), recipient: empfaenger,
      date: heute(), title: a.lang === "en" ? "Payment reminder" : "Zahlungserinnerung", tone: "reminder",
      invoice: { number: "RE-2026-042", date: inTagen(-30), dueDate: inTagen(-16), grossCents: 374_850 },
      paragraphs: [
        "Sehr geehrte Damen und Herren,",
        "sicher ist es Ihrer Aufmerksamkeit entgangen: Die Rechnung RE-2026-042 ist seit 16 Tagen fällig. Wir bitten Sie, den offenen Betrag bis zum genannten Datum zu überweisen.",
        "Mit freundlichen Grüßen",
        a.name || "Agentur",
      ],
      costs: { principalCents: 374_850, feeCents: 0, interestCents: 0, totalCents: 374_850, interest: null },
      deadline: inTagen(7), bank: { ...bank, accountHolder: a.name || "Agentur" }, accent: a.accent, logo: a.logo, layout,
    });
  }
  if (art === "offer") {
    return renderOfferSvg({
      language: a.lang, locale: a.lang === "en" ? "en-GB" : "de-DE", currency: "EUR", number: "AN-2026-012", date: heute(), validUntil: inTagen(14),
      sender: absender(a), footerLines: fuss(a), recipient: { ...empfaenger, addressLines: ["z. Hd. Lena Beispiel", ...empfaenger.addressLines] },
      title: "Herbstkampagne Hautpflege", intro: ["Vielen Dank für Ihre Anfrage. Gern schlagen wir folgende Creator vor:"],
      groups: [{ heading: "Mara Linden", lines: [
        { description: "Reel · Instagram · Morgenroutine", quantity: 1, unitPriceCents: 180_000, surcharges: [] },
        { description: "Story · Instagram", quantity: 3, unitPriceCents: 45_000, surcharges: [] },
      ] }],
      commissionIncluded: true, agencyFeeBp: null, basisText: "Alle Preise netto zuzüglich Umsatzsteuer in gesetzlicher Höhe.", terms: ["Zahlungsziel 14 Tage nach Veröffentlichung."],
      accent: a.accent, logo: a.logo, layout,
    });
  }
  return vertragSeiten(a, layout);
}

/** Vertrag: Seitenmodell aus contract-pdf.ts als SVG (Text und Linien; das Logo als Bild aus der Adresse der Agentur) */
function vertragSeiten(a: VorschauAgentur, layout: BelegLayout): string[] {
  const html = [
    "<h1>Kooperationsvertrag</h1>",
    "<p>zwischen <b>Beispielmarke GmbH</b>, Musterstraße 1, 10115 Berlin (nachfolgend „Marke“) und <b>Mara Linden</b> (nachfolgend „Creator“), vermittelt durch die Agentur.</p>",
    "<h2>§ 1 Gegenstand</h2><p>Der Creator erstellt für die Marke Beiträge nach der Leistungsbeschreibung in § 2 und veröffentlicht sie auf Instagram.</p>",
    "<h2>§ 2 Leistungen</h2><ul><li>1 Reel (Instagram), Veröffentlichung bis 30.11.2026</li><li>3 Storys (Instagram)</li></ul>",
    "<h2>§ 3 Vergütung</h2><p>Die Marke zahlt 2.250,00 € netto zuzüglich Umsatzsteuer. Die Rechnung stellt die Agentur.</p>",
    "<h2>§ 4 Nutzungsrechte</h2><p>Die Marke darf die Beiträge drei Monate ab Veröffentlichung in ihren eigenen Kanälen nutzen.</p>",
  ].join("");
  const p = contractPagePreview({
    language: a.lang, title: "Kooperationsvertrag", header: `${a.name || "Agentur"} · V-0042`, accent: a.accent, bodyHtml: html, signatures: [],
    logo: a.logo ? { jpeg: a.logo.jpeg, width: a.logo.width, height: a.logo.height } : null, layout,
  });
  const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  // Bilder in einem SVG als <img> laden keine fremden Adressen: das Logo deshalb als eingebettete JPEG-Daten
  let logoDaten = "";
  if (a.logo) {
    let bin = "";
    for (let i = 0; i < a.logo.jpeg.length; i += 0x8000) bin += String.fromCharCode(...a.logo.jpeg.subarray(i, i + 0x8000));
    logoDaten = `data:image/jpeg;base64,${btoa(bin)}`;
  }
  const farbe = (c: readonly number[]) => `rgb(${c.map((v) => Math.round(v * 255)).join(",")})`;
  return p.pages.map((items) => {
    const teile = items.map((it) => {
      if (it.t === "text") return `<text x="${it.x}" y="${p.height - it.y}" font-family="Helvetica, Arial, sans-serif" font-size="${it.size}" font-weight="${it.bold ? 700 : 400}" font-style="${it.italic ? "italic" : "normal"}" fill="${farbe(it.color)}">${esc(it.text)}</text>`;
      if (it.t === "line") return `<line x1="${it.x1}" y1="${p.height - it.y1}" x2="${it.x2}" y2="${p.height - it.y2}" stroke="${farbe(it.color)}" stroke-width="${it.width}"/>`;
      return logoDaten ? `<image href="${logoDaten}" x="${it.x}" y="${p.height - it.y - it.h}" width="${it.w}" height="${it.h}" preserveAspectRatio="xMinYMin meet"/>` : "";
    }).join("");
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${p.width} ${p.height}" width="${p.width}" height="${p.height}"><rect width="100%" height="100%" fill="#fff"/>${teile}</svg>`;
  });
}
